import { apiClient } from './client'

export interface DeploymentSkillFrontmatter {
  name?: string
  description?: string
  version?: string
  mcp_tools?: string[]
}

export interface DeploymentSkillSummary {
  id: string
  name: string
  description: string
  version: string
  mcpTools: string[]
  updatedAt?: string
  valid: boolean
  validationErrors: string[]
  filesCount: number
}

export interface DeploymentSkillFile {
  path: string
  content?: string
  size: number
  updatedAt?: string
}

export interface DeploymentSkillDocument extends DeploymentSkillSummary {
  content: string
  body: string
  frontmatter: DeploymentSkillFrontmatter
  files: DeploymentSkillFile[]
}

export interface SkillCatalogEntry {
  id: string
  name: string
  githubUrl: string
  description: string
  maintainer: string
  iconUrl: string
  branch: string
  tags: string[]
  version: string
  verified: boolean
  active: boolean
  metadata?: Record<string, unknown>
  createdAt?: string
  updatedAt?: string
}

export interface SkillInstallation {
  id: string
  skillName: string
  skillVersion: string
  status: string
  configOverrides?: Record<string, unknown>
  installedComponents?: Record<string, unknown>
  installedAt?: string
  installedBy?: string
}

export interface ImportGitHubSkillPayload {
  githubUrl: string
  branch?: string
  path?: string
  id?: string
  overwrite?: boolean
}

function mapSkillSummary(raw: Record<string, unknown>): DeploymentSkillSummary {
  return {
    id: String(raw?.id ?? ''),
    name: String(raw?.name ?? raw?.id ?? ''),
    description: String(raw?.description ?? ''),
    version: String(raw?.version ?? ''),
    mcpTools: Array.isArray(raw?.mcp_tools)
      ? raw.mcp_tools.map((value: unknown) => String(value))
      : [],
    updatedAt: typeof raw?.updated_at === 'string' ? raw.updated_at : undefined,
    valid: raw?.valid !== false,
    validationErrors: Array.isArray(raw?.validation_errors)
      ? raw.validation_errors.map((value: unknown) => String(value))
      : [],
    filesCount: typeof raw?.files_count === 'number' ? raw.files_count : 0,
  }
}

function mapSkillFile(raw: Record<string, unknown>): DeploymentSkillFile {
  return {
    path: String(raw?.path ?? ''),
    content: typeof raw?.content === 'string' ? raw.content : undefined,
    size: typeof raw?.size === 'number' ? raw.size : 0,
    updatedAt: typeof raw?.updated_at === 'string' ? raw.updated_at : undefined,
  }
}

function mapSkillDocument(raw: Record<string, unknown>): DeploymentSkillDocument {
  const frontmatter =
    raw?.frontmatter && typeof raw.frontmatter === 'object'
      ? (raw.frontmatter as Record<string, unknown>)
      : null

  return {
    ...mapSkillSummary(raw),
    content: String(raw?.content ?? ''),
    body: String(raw?.body ?? ''),
    frontmatter:
      frontmatter
        ? {
            name: typeof frontmatter.name === 'string' ? frontmatter.name : undefined,
            description:
              typeof frontmatter.description === 'string' ? frontmatter.description : undefined,
            version: typeof frontmatter.version === 'string' ? frontmatter.version : undefined,
            mcp_tools: Array.isArray(frontmatter.mcp_tools)
              ? frontmatter.mcp_tools.map((value: unknown) => String(value))
              : [],
          }
        : {},
    files: Array.isArray(raw?.files)
      ? raw.files.map((value: unknown) =>
          mapSkillFile((value ?? {}) as Record<string, unknown>),
        )
      : [],
  }
}

function mapSkillCatalogEntry(raw: Record<string, unknown>): SkillCatalogEntry {
  return {
    id: String(raw?.id ?? ''),
    name: String(raw?.name ?? raw?.id ?? ''),
    githubUrl: String(raw?.github_url ?? ''),
    description: String(raw?.description ?? ''),
    maintainer: String(raw?.maintainer ?? ''),
    iconUrl: String(raw?.icon_url ?? ''),
    branch: String(raw?.branch ?? ''),
    tags: Array.isArray(raw?.tags) ? raw.tags.map((value: unknown) => String(value)) : [],
    version: String(raw?.version ?? ''),
    verified: raw?.verified === true,
    active: raw?.active !== false,
    metadata:
      raw?.metadata && typeof raw.metadata === 'object'
        ? (raw.metadata as Record<string, unknown>)
        : undefined,
    createdAt: typeof raw?.created_at === 'string' ? raw.created_at : undefined,
    updatedAt: typeof raw?.updated_at === 'string' ? raw.updated_at : undefined,
  }
}

function mapSkillInstallation(raw: Record<string, unknown>): SkillInstallation {
  return {
    id: String(raw?.id ?? ''),
    skillName: String(raw?.skill_name ?? raw?.skillName ?? ''),
    skillVersion: String(raw?.skill_version ?? raw?.skillVersion ?? ''),
    status: String(raw?.status ?? ''),
    configOverrides:
      raw?.config_overrides && typeof raw.config_overrides === 'object'
        ? (raw.config_overrides as Record<string, unknown>)
        : undefined,
    installedComponents:
      raw?.installed_components && typeof raw.installed_components === 'object'
        ? (raw.installed_components as Record<string, unknown>)
        : undefined,
    installedAt: typeof raw?.installed_at === 'string' ? raw.installed_at : undefined,
    installedBy: typeof raw?.installed_by === 'string' ? raw.installed_by : undefined,
  }
}

class SkillsAPI {
  async listSkillCatalog(): Promise<SkillCatalogEntry[]> {
    const response = await apiClient.get<Record<string, unknown>[]>('/skills/catalog')
    return Array.isArray(response)
      ? response.map((item) => mapSkillCatalogEntry(item ?? {}))
      : []
  }

  async listDeploymentSkillInstallations(deploymentId: string): Promise<SkillInstallation[]> {
    const response = await apiClient.get<Record<string, unknown>[]>(
      `/deployment/${deploymentId}/skills`,
    )
    return Array.isArray(response)
      ? response.map((item) => mapSkillInstallation(item ?? {}))
      : []
  }

  async installCatalogSkill(
    skillName: string,
    deploymentId: string,
    configOverrides?: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return apiClient.post<Record<string, unknown>>(`/skills/catalog/${encodeURIComponent(skillName)}/install`, {
      deployment_id: deploymentId,
      config_overrides: configOverrides,
    })
  }

  async uninstallCatalogSkill(
    skillName: string,
    deploymentId: string,
    cleanupData = false,
  ): Promise<Record<string, unknown>> {
    return apiClient.post<Record<string, unknown>>(`/skills/catalog/${encodeURIComponent(skillName)}/uninstall`, {
      deployment_id: deploymentId,
      cleanup_data: cleanupData,
    })
  }

  async importGitHubSkill(
    orgId: string,
    deploymentId: string,
    payload: ImportGitHubSkillPayload,
  ): Promise<DeploymentSkillDocument> {
    const response = await apiClient.post<Record<string, unknown>>(
      `/organization/${orgId}/deployment/${deploymentId}/skills/import-github`,
      {
        github_url: payload.githubUrl,
        branch: payload.branch,
        path: payload.path,
        id: payload.id,
        overwrite: payload.overwrite ?? false,
      },
    )

    return mapSkillDocument(response ?? {})
  }

  async listDeploymentSkills(
    orgId: string,
    deploymentId: string,
  ): Promise<DeploymentSkillSummary[]> {
    let response: Record<string, unknown>[]
    try {
      response = await apiClient.get<Record<string, unknown>[]>(
        `/organization/${orgId}/deployment/${deploymentId}/skills`,
      )
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      if (
        message.includes('no storage configured for deployment') ||
        message.includes('API Error 404:')
      ) {
        return []
      }
      throw error
    }

    return Array.isArray(response) ? response.map((item) => mapSkillSummary(item ?? {})) : []
  }

  async getDeploymentSkill(
    orgId: string,
    deploymentId: string,
    skillId: string,
  ): Promise<DeploymentSkillDocument> {
    const response = await apiClient.get<Record<string, unknown>>(
      `/organization/${orgId}/deployment/${deploymentId}/skills/${skillId}`,
    )

    return mapSkillDocument(response ?? {})
  }
}

export const skillsAPI = new SkillsAPI()
